import { isAuthenticated } from "../utils/auth";
import { getUserRole } from "../utils/sessionStore";
import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Typography, 
  Box, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  Fade,
  Grow,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  FiArrowRight, 
  FiShield, 
  FiZap, 
  FiUsers, 
  FiAward,
  FiPlay,
  FiStar
} from 'react-icons/fi';
import { 
  MdRocketLaunch, 
  MdAutoAwesome, 
} from 'react-icons/md';
import './Content.css';

const MainContent = () => {
  const [showCards, setShowCards] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleStartClick = () => {
    const role = getUserRole();

    if (isAuthenticated()) {
      setShowCards(true);
      if (["Hr", "ProjectManager", "AdminController"].includes(role)) {
        navigate('/admin-dashboard');
      } else if (role === "Distributor") {
        navigate('/dar-List');
      } else {
        alert('Unauthorized access');
      }
    } else {
      alert('Please login to start');
    }
  };

  const handleManageClick = () => {
    if (isAuthenticated()) {
      window.scrollTo(0, 0);
      navigate('/disinfection-analysis');
    } else {
      alert('Please login to start');
    }
  };

  const handleViewTutorialClick = () => {
    window.scrollTo(0, 0);
    navigate('/tutorial');
  };

  const stats = [
    { number: "99.9%", label: "System Uptime", icon: <FiShield /> },
    { number: "1,200+", label: "Active Users", icon: <FiUsers /> },
    { number: "24/7", label: "Admin Support", icon: <FiAward /> },
    { number: "5sec", label: "Response Time", icon: <FiZap /> }
  ];

  return (
    <Box style={styles.mainContainer}>
      {!showCards ? (
        <>
          {/* Hero Section */}
          <Container maxWidth="xl" style={styles.heroContainer}>
            <Fade in={isLoaded} timeout={1000}>
              <Box style={styles.heroContent} className="hero-content-responsive">
                <Box style={styles.heroText} className="hero-text-responsive">
                  <Chip 
                    label="Advanced Admin Controls" 
                    style={styles.badge}
                    size="medium"
                    onClick={() => navigate('/admin-setup-guide')}
                  />

                  <Grow in={isLoaded} timeout={1050}>
                    <Box style={styles.robotHeroLine} className="robot-hero-line-responsive">
                      <Typography component="p" variant="overline" style={styles.robotHeroEyebrow}>
                        Powered by
                      </Typography>
                      <Typography component="h2" style={styles.robotHeroNames}>
                        <Box component="span" style={styles.robotNameHighlight}>
                        Spotbot Lite
                        </Box>
                        <Box component="span" style={styles.robotNameSeparator}>
                          {" · "}
                        </Box>
                        <Box component="span" style={styles.robotNameHighlight}>
                        Spotbot Xtreme
                        </Box>
                      </Typography>
                    </Box>
                  </Grow>
                  
                  <Grow in={isLoaded} timeout={1200}>
                    <Typography variant="h1" style={styles.mainHeading} className="main-heading-responsive">
                      Manage Your
                      <Box component="span" style={styles.gradientText}>
                        {" "}System{" "}
                      </Box>
                      Like Never Before
                    </Typography>
                  </Grow>

                  <Fade in={isLoaded} timeout={1400}>
                    <Typography variant="h5" style={styles.subHeading} className="sub-heading-responsive">
                      Comprehensive administrative control for user management and system monitoring.
                      <br />
                      Spotless AI Admin Portal - Your command center for everything.
                    </Typography>
                  </Fade>

                  <Fade in={isLoaded} timeout={1600}>
                    <Box style={styles.ctaSection} className="cta-section-responsive">
                      <Button 
                        onClick={handleStartClick}
                        style={styles.primaryButton}
                        className="hero-btn-responsive"
                        size="large"
                        endIcon={<FiArrowRight size={20} />}
                      >
                        Access Dashboard
                      </Button>
                      
                      <Button 
                        onClick={handleViewTutorialClick}
                        style={styles.secondaryButton}
                        className="hero-btn-responsive"
                        size="large"
                        startIcon={<FiPlay size={18} />}
                      >
                        View Tutorial
                      </Button>
                    </Box>
                  </Fade>

                  <Fade in={isLoaded} timeout={1800}>
                    <Box style={styles.trustIndicators}>
                      <Typography variant="caption" style={styles.trustText}>
                        Trusted by administrators across 200+ organizations
                      </Typography>
                      <Box style={styles.stars}>
                        {[1,2,3,4,5].map((star) => (
                          <FiStar key={star} size={16} style={styles.starIcon} />
                        ))}
                        <Typography variant="caption" style={styles.rating}>
                          4.8/5 (Admin satisfaction rating)
                        </Typography>
                      </Box>
                    </Box>
                  </Fade>
                </Box>

                {/* Hero Visual */}
                <Fade in={isLoaded} timeout={2000}>
                  <Box style={styles.heroVisual} className="hero-visual-responsive" onClick={() => navigate('/admin-dashboard')}>
                    <Box style={styles.floatingCard} className="floating-card-responsive">
                      <Box style={styles.cardGlow}></Box>
                      <MdAutoAwesome size={64} style={styles.cardIcon} />
                      <Typography variant="h6" style={styles.cardTitle}>
                        Admin Control
                      </Typography>
                      <Typography variant="body2" style={styles.cardSubtitle}>
                        Complete Management
                      </Typography>
                    </Box>
                  </Box>
                </Fade>
              </Box>
            </Fade>
          </Container>

          {/* Stats Section */}
          <Container maxWidth="lg" style={styles.statsContainer}>
            <Fade in={isLoaded} timeout={2200}>
              <Grid container spacing={4}>
                {stats.map((stat, index) => (
                  <Grid item xs={6} md={3} key={index}>
                    <Grow in={isLoaded} timeout={2400 + index * 200}>
                      <Card style={styles.statCard}>
                        <CardContent style={styles.statContent}>
                          <Box style={styles.statIcon} className="stat-icon-circle">
                            {stat.icon}
                          </Box>
                          <Typography variant="h4" style={styles.statNumber}>
                            {stat.number}
                          </Typography>
                          <Typography variant="body2" style={styles.statLabel}>
                            {stat.label}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grow>
                  </Grid>
                ))}
              </Grid>
            </Fade>
          </Container>

          {/* CTA Section */}
          <Container maxWidth="md" style={styles.finalCtaContainer}>
            <Fade in={isLoaded} timeout={3200}>
              <Card style={styles.ctaCard}>
                <CardContent style={styles.ctaCardContent}>
                  <Typography variant="h4" style={styles.ctaTitle}>
                    Ready to Streamline Your Administration?
                  </Typography>
                  <Typography variant="body1" style={styles.ctaDescription}>
                    Join hundreds of administrators who trust our platform for efficient user and system management.
                  </Typography>
                  <Button 
                    onClick={handleManageClick}
                    style={styles.ctaButton}
                    size="large"
                    endIcon={<MdRocketLaunch size={20} />}
                  >
                    Start Managing Now
                  </Button>
                </CardContent>
              </Card>
            </Fade>
          </Container>
        </>
      ) : null}
    </Box>
  );
};

const styles = {
  mainContainer: {
    background: "linear-gradient(180deg, #fafbfc 0%, #f1f5f9 100%)",
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(236, 72, 153, 0.1) 0%, transparent 50%)",
      pointerEvents: "none",
    }
  },
  
  heroContainer: {
    paddingTop: "80px",
    paddingBottom: "80px",
    position: "relative",
    zIndex: 1,
  },
  
  heroContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "60px",
    flexWrap: "wrap",
  },
  
  heroText: {
    flex: 1,
    minWidth: "500px",
    maxWidth: "600px",
  },
  
  badge: {
    background: "linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(236, 72, 153, 0.12) 100%)",
    color: "#3B82F6",
    border: "1px solid rgba(59, 130, 246, 0.25)",
    fontSize: "0.875rem",
    fontWeight: 600,
    padding: "8px 16px",
    marginBottom: "20px",
    backdropFilter: "blur(10px)",
  },

  robotHeroLine: {
    marginBottom: "28px",
  },

  robotHeroEyebrow: {
    color: "#64748b",
    fontWeight: 700,
    letterSpacing: "0.12em",
    marginBottom: "6px",
    fontSize: "0.7rem",
  },

  robotHeroNames: {
    margin: 0,
    fontSize: "clamp(1.35rem, 3.2vw, 2rem)",
    fontWeight: 800,
    lineHeight: 1.25,
    letterSpacing: "-0.02em",
  },

  robotNameHighlight: {
    background: "linear-gradient(to right, #2563eb 0%, #7c3aed 45%, #db2777 100%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    textShadow: "0 1px 0 rgba(15, 23, 42, 0.06)",
  },

  robotNameSeparator: {
    color: "#94a3b8",
    fontWeight: 600,
    WebkitTextFillColor: "initial",
    background: "none",
  },
  
  mainHeading: {
    fontSize: "clamp(2.5rem, 5vw, 4rem)",
    fontWeight: 800,
    lineHeight: 1.1,
    color: "#0f172a",
    marginBottom: "24px",
    letterSpacing: "-0.02em",
  },
  
  gradientText: {
    background: "linear-gradient(to right, #3B82F6 0%, #EC4899 100%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontWeight: 900,
  },
  
  subHeading: {
    color: "#475569",
    lineHeight: 1.6,
    marginBottom: "40px",
    fontWeight: 400,
    maxWidth: "540px",
  },
  
  ctaSection: {
    display: "flex",
    gap: "16px",
    marginBottom: "48px",
    flexWrap: "wrap",
  },
  
  primaryButton: {
    background: "linear-gradient(to right, #3B82F6 0%, #EC4899 100%)",
    color: "white",
    padding: "16px 32px",
    borderRadius: "16px",
    textTransform: "none",
    fontSize: "1.1rem",
    fontWeight: 600,
    boxShadow: "0 8px 25px rgba(59, 130, 246, 0.35)",
    border: "none",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      background: "linear-gradient(to right, #2563eb 0%, #db2777 100%)",
      transform: "translateY(-3px)",
      boxShadow: "0 12px 35px rgba(236, 72, 153, 0.4)",
    }
  },
  
  secondaryButton: {
    color: "#3B82F6",
    padding: "16px 32px",
    borderRadius: "16px",
    textTransform: "none",
    fontSize: "1.1rem",
    fontWeight: 600,
    border: "2px solid rgba(59, 130, 246, 0.3)",
    background: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(10px)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      background: "rgba(59, 130, 246, 0.08)",
      borderColor: "rgba(59, 130, 246, 0.5)",
      transform: "translateY(-2px)",
    }
  },
  
  trustIndicators: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  
  trustText: {
    color: "#475569",
    fontWeight: 500,
    fontSize: "0.875rem",
  },
  
  stars: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  
  starIcon: {
    color: "#fbbf24",
    fill: "currentColor",
  },
  
  rating: {
    color: "#475569",
    marginLeft: "8px",
    fontWeight: 500,
  },
  
  heroVisual: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
    position: "relative",
  },
  
  floatingCard: {
    background: "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(20px)",
    borderRadius: "24px",
    padding: "40px",
    textAlign: "center",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.1)",
    position: "relative",
    transform: "translateY(0)",
    animation: "float 6s ease-in-out infinite",
    minWidth: "280px",
  },
  
  cardGlow: {
    position: "absolute",
    top: "-50%",
    left: "-50%",
    right: "-50%",
    bottom: "-50%",
    background: "conic-gradient(from 0deg, #3B82F6, #EC4899, #3B82F6)",
    borderRadius: "50%",
    opacity: 0.1,
    animation: "rotate 10s linear infinite",
    zIndex: -1,
  },
  
  cardIcon: {
    color: "#3B82F6",
    marginBottom: "16px",
  },
  
  cardTitle: {
    color: "#0f172a",
    fontWeight: 700,
    marginBottom: "8px",
  },
  
  cardSubtitle: {
    color: "#475569",
    fontWeight: 500,
  },
  
  statsContainer: {
    paddingBottom: "80px",
    position: "relative",
    zIndex: 1,
  },
  
  statCard: {
    background: "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(20px)",
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 8px 25px rgba(0, 0, 0, 0.08)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.12)",
    }
  },
  
  statContent: {
    textAlign: "center",
    padding: "32px 24px",
  },
  
  statIcon: {
    color: "#3B82F6",
    fontSize: "1.5rem",
    marginBottom: "16px",
  },
  
  statNumber: {
    color: "#0f172a",
    fontWeight: 800,
    marginBottom: "8px",
    background: "linear-gradient(to right, #3B82F6 0%, #EC4899 100%)",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  
  statLabel: {
    color: "#475569",
    fontWeight: 600,
    textTransform: "uppercase",
    fontSize: "0.875rem",
    letterSpacing: "0.05em",
  },
  
  finalCtaContainer: {
    paddingBottom: "80px",
    position: "relative",
    zIndex: 1,
  },
  
  ctaCard: {
    background: "linear-gradient(to right, #3B82F6 0%, #EC4899 100%)",
    borderRadius: "24px",
    border: "none",
    boxShadow: "0 25px 50px rgba(59, 130, 246, 0.35)",
  },
  
  ctaCardContent: {
    padding: "64px 48px",
    textAlign: "center",
    color: "white",
  },
  
  ctaTitle: {
    color: "white",
    fontWeight: 800,
    marginBottom: "16px",
  },
  
  ctaDescription: {
    color: "rgba(255, 255, 255, 0.9)",
    marginBottom: "32px",
    fontSize: "1.125rem",
    lineHeight: 1.6,
  },
  
  ctaButton: {
    background: "rgba(255, 255, 255, 0.2)",
    color: "white",
    padding: "16px 32px",
    borderRadius: "16px",
    textTransform: "none",
    fontSize: "1.1rem",
    fontWeight: 600,
    border: "2px solid rgba(255, 255, 255, 0.3)",
    backdropFilter: "blur(10px)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      background: "rgba(255, 255, 255, 0.3)",
      transform: "translateY(-3px)",
      boxShadow: "0 12px 35px rgba(255, 255, 255, 0.2)",
    }
  },
  
  // Responsive styles
  "@media (max-width: 768px)": {
    heroContent: {
      flexDirection: "column",
      textAlign: "center",
      gap: "40px",
    },
    heroText: {
      minWidth: "auto",
      maxWidth: "100%",
    },
    ctaSection: {
      justifyContent: "center",
    },
    primaryButton: {
      width: "100%",
      maxWidth: "300px",
    },
    secondaryButton: {
      width: "100%",
      maxWidth: "300px",
    },
    floatingCard: {
      minWidth: "240px",
    },
    ctaCardContent: {
      padding: "48px 32px",
    },
  }
};

export default MainContent;
